import '../video-bar/video-bar.component.scss';
import './video-landing.component.scss';

import type { FunctionComponent } from 'preact';
import { useEffect } from 'preact/hooks';
import { Provider } from 'react-redux';

import { LoginType } from '../../../auth/models/auth.model';
import { selectIsAuthenticated } from '../../../auth/store/auth.selectors';
import { checkAuthentication, initializeCustomer } from '../../../auth/store/auth.thunk';
import { SearchBar } from '../../../common/components/search-bar/search-bar.component';
import { SpinnerComponent } from '../../../common/components/spinner/spinner.component';
import { store } from '../../../store';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { decodeBase64 } from '../../../utils/base-64.utils';
import { getSearchParams } from '../../../utils/window.utils';
import type { VideoBarRequest } from '../../models/video-bar.interface';
import { selectVideoBarById, selectVideoBarsLoading } from '../../store/content.selectors';
import { loadVideoBar } from '../../store/content.thunk';
import type { VideoBarProps } from '../video-bar/video-bar.component';
import { VideoBarGrid } from '../video-bar/video-bar-grid/video-bar-grid.component';

const VideoLandingContent: FunctionComponent = () => {
  const param = getSearchParams().get('encoded-request') as string;
  const props: VideoBarProps = decodeBase64(param) as VideoBarProps;
  const cssVars = {
    '--cardBorderRadius': props.cardBorderRadius,
    '--columnCount': props.columnCount,
    '--textColor': props.textColor,
    '--background': props.background,
    '--badgeColor': props.badgeColor,
    '--badgeBackground': props.badgeBackground,
    '--loadingBackground': props.loadingBackground,
    '--verticalPadding': props.verticalPadding,
    '--horizontalPadding': props.horizontalPadding,
    '--thumbnailHeight': props.thumbnailHeight,
  };
  const generateVideoBarId = (): string => {
    // Create a unique ID based on component inputs to identify this specific video bar instance
    const inputs = [
      props.title || '',
      props.description || '',
      props.tags || '',
      props.sort || '',
      props.maxVideos || '',
      props.allVideos || false,
      props.displayType || '',
      props.columnCount || '',
      Math.random().toString(36).substr(2, 9),
    ].join('|');

    return btoa(inputs)
      .replace(/[^a-zA-Z0-9]/g, '')
      .substr(0, 16);
  };
  const instanceId = generateVideoBarId();

  const request: VideoBarRequest = {
    title: props.title,
    description: props.description,
    tags: props.tags,
    sort: props.sort,
    allVideos: props.allVideos,
    maxVideos: 100, // Defaulting to get "all" videos
    badgeText: props.badgeText,
    titleSize: props.titleSize,
    displayType: props.displayType,
    columnCount: props.columnCount,
    showViewAll: props.showViewAll,
    showBadge: props.showBadge,
    showVideoTitle: props.showVideoTitle,
    showVideoDate: props.showVideoDate,
    showVideoArtist: props.showVideoArtist,
    showCardDropShadow: props.showCardDropShadow,
    maxWidth: props.maxWidth,
    instanceId,
  };

  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const loading: boolean = useAppSelector(selectVideoBarsLoading);
  const videoBarResp = useAppSelector(selectVideoBarById(instanceId));
  const { content: videoBar } = videoBarResp || {};

  useEffect(() => {
    dispatch(checkAuthentication({ loginTypes: [LoginType.CUSTOMER] }));
    dispatch(initializeCustomer());
    dispatch(loadVideoBar(request));
  }, [dispatch]);

  const handleSearch = (query: string) => {
    request.query = query;
    dispatch(loadVideoBar(request));
  };

  return (
    <div class="single-video-bar-wrapper single-video-landing" style={cssVars}>
      {(loading || !videoBar || videoBar.length === 0) && (
        <section className="single-loading">
          <SpinnerComponent />
        </section>
      )}

      {videoBar && videoBar.length > 0 && (
        <div
          class={`single-content-section ${props.maxWidth || props.maxWidth ? 'page-width' : ''}`}
        >
          {props.title && (
            <h2 class={`title ${props.titleSize} single-video-collection-title`}>{props.title}</h2>
          )}
          {props.description && (
            <div
              class="single-video-collection-description"
              dangerouslySetInnerHTML={{ __html: props.description }}
            />
          )}

          <SearchBar onSearch={handleSearch} onClear={handleSearch} />

          <VideoBarGrid
            request={request}
            videoBar={videoBar}
            showViewAll={false}
            showBadge={props.showBadge}
            showVideoTitle={props.showVideoTitle}
            showVideoDate={props.showVideoArtist}
            showVideoArtist={props.showVideoArtist}
            showCardDropShadow={props.showCardDropShadow}
            badgeText={props.badgeText}
          />
        </div>
      )}
    </div>
  );
};

export const VideoLandingComponent: FunctionComponent = () => {
  return (
    <Provider store={store}>
      <VideoLandingContent />
    </Provider>
  );
};
